import {
  faSpinner,
  faStop,
  faVolumeHigh,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getSurvicateSurveyUrl,
  SURVICATE_INSTANCE,
} from '../Analytics/SurvicateAnalyticsPlatform';
import { QuestionResponseApi } from '../API/QuestionResponseApi';
import { ReadingDataApi } from '../API/ReadingDataApi';
import { useAuth } from '../Auth/AuthProvider';
import LoadingIcon from '../Components/LoadingIcon';
import TextToSpeech from '../Components/TextToSpeech';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import { AudioPlayerState, useAudio } from '../Context/AudioContext';
import { useBalance } from '../Context/BalanceProvider';
import { useSession } from '../Context/ReadingSessionProvider';
import { useNavigate } from '../ProtectedRoute';
import { Article } from '../Types/Article';
import { Transaction } from '../Types/Money';
import { Question, QuestionType } from '../Types/Question';
import { AnswerState, QuestionResponse } from '../Types/QuestionResponse';
import { UserAchievement } from '../Types/UserAchievement';
import { UserLootBox } from '../Types/UserLootBox';
import QuizActionButton from './QuizComponents/QuizActionButton';
import QuizProgressBar from './QuizComponents/QuizProgressBar';
import QuizQuestionOption from './QuizComponents/QuizQuestionOption';
import QuizResultSummary from './QuizComponents/QuizResultSummary';

interface QuizViewProps {
  article: Article;
  onLoad?: (
    currentQuestion: Question,
    questions: Question[],
    questionResponses: QuestionResponse[],
  ) => void;
  onQuestionAnswered?: (
    currentQuestion: Question,
    currentQuestionResponse: QuestionResponse,
    questions: Question[],
    questionResponses: QuestionResponse[],
    userAchievements: UserAchievement[],
    lootBox: UserLootBox | null,
  ) => void;
  onNextQuestion?: (currentQuestion: Question, nextQuestion: Question) => void;
  onQuizComplete?: (
    questions: Question[],
    questionResponses: QuestionResponse[],
  ) => void;
  onQuizSectionComplete?: (section: 'guiding' | 'assessment') => void;
}

enum AssessmentState {
  NotStarted = 'notstarted',
  GuidingQuestions = 'guidingquestions',
  AssessmentQuestions = 'assessmentquestions',
  Complete = 'complete',
}

const QuizView: React.FC<QuizViewProps> = ({
  article,
  onLoad,
  onQuestionAnswered,
  onNextQuestion,
  onQuizComplete,
  onQuizSectionComplete,
}) => {
  const { userId, user, isUserAnonymous } = useAuth();
  const { trackEvent } = useAnalytics();
  const { getRewardsByEventKey, updateBalances } = useBalance();
  const { flushActivityData } = useSession();
  const [questions, setQuestions] = useState<Question[]>();
  const [questionResponses, setQuestionResponses] =
    useState<QuestionResponse[]>();
  const [currentQuestionId, setCurrentQuestionId] = useState<string>();
  const { audioManager } = useAudio();
  const [playbackState, setPlaybackState] = useState<AudioPlayerState>(
    AudioPlayerState.Stop,
  );
  const [isPlaybackSectionLoaded, setIsPlaybackSectionLoaded] = useState(false);
  const [lootBox, setLootBox] = useState<UserLootBox | null>(undefined);
  const [showLootBox, setShowLootBox] = useState(false);
  const [articleRewards, setArticleRewards] = useState<
    Transaction[] | undefined
  >(undefined);
  const [questionStartTime, setQuestionStartTime] = useState<Date | null>(null);
  const guidingQuestions = useMemo(
    () =>
      questions?.filter(
        (question) => question.questionType === QuestionType.GUIDING,
      ),
    [questions],
  );
  const assessmentQuestions = useMemo(
    () =>
      questions?.filter(
        (question) => question.questionType === QuestionType.ASSESSMENT,
      ),
    [questions],
  );
  const questionsById = useMemo<Record<string, Question>>(
    () =>
      questions &&
      Object.fromEntries(questions?.map((question) => [question.id, question])),
    [questions],
  );
  const guidingQuestionAnswers = useMemo(
    () =>
      questionsById &&
      questionResponses?.filter(
        (response) =>
          questionsById[response.questionId]?.questionType ===
          QuestionType.GUIDING,
      ),
    [questionResponses],
  );
  const assessmentQuestionAnswers = useMemo(
    () =>
      questionsById &&
      questionResponses?.filter(
        (response) =>
          questionsById[response.questionId]?.questionType ===
          QuestionType.ASSESSMENT,
      ),
    [questionResponses],
  );

  const currentQuestion = useMemo(() => {
    return (
      currentQuestionId &&
      questions?.find((question) => question.id === currentQuestionId)
    );
  }, [questions, currentQuestionId]);
  const currentQuestionResponse = useMemo(() => {
    return (
      currentQuestion &&
      questionResponses?.find(
        (response) => response.questionId === currentQuestion.id,
      )
    );
  }, [questionResponses, currentQuestion]);

  const assessmentState = useMemo<AssessmentState>(() => {
    if (questionResponses?.length === 0) {
      return AssessmentState.NotStarted;
    }
    if (currentQuestion?.questionType === QuestionType.GUIDING) {
      return AssessmentState.GuidingQuestions;
    }
    if (currentQuestion?.questionType === QuestionType.ASSESSMENT) {
      return AssessmentState.AssessmentQuestions;
    }
    return AssessmentState.Complete;
  }, [questionResponses, currentQuestion]);

  const isLoaded = useMemo(() => {
    return questionResponses && questions;
  }, [questionResponses, questions]);

  useEffect(() => {
    getRewardsByEventKey(`quiz-submitted-${article.id}`).then((rewards) => {
      setArticleRewards(rewards);
    });
  }, [article?.quizGeneratedAt?.getTime()]);

  useEffect(() => {
    const questionResponseApi = new QuestionResponseApi();
    questionResponseApi
      .fetchQuestionResponses(article.id)
      .then((questionResponses) => {
        setQuestionResponses(questionResponses);

        const questions = article.questions.sort((a, b) => a.order - b.order);
        const answerStateByQuestionId = questions.reduce(
          (acc: Record<string, AnswerState>, question: Question) => {
            const answer = questionResponses.find(
              (answer) => answer.questionId === question.id,
            );
            acc[question.id] = answer?.answerState || 'not answered';
            return acc;
          },
          {},
        );

        const initialQuestionResponses: QuestionResponse[] =
          article.questions.map(
            (question) =>
              ({
                questionId: question.id,
                answerState: answerStateByQuestionId[question.id],
                chosenOptionIndex: null,
                isCompleted:
                  answerStateByQuestionId[question.id] !== 'not answered',
              }) as QuestionResponse,
          );

        const firstUnansweredQuestion = questions
          .sort(
            (a, b) =>
              // Guiding questions first
              (a.questionType === QuestionType.GUIDING ? -1000 : 1000) -
              (b.questionType === QuestionType.GUIDING ? -1000 : 1000) +
              // Lowest order first
              a.order -
              b.order,
          )
          .find((question) =>
            initialQuestionResponses.some(
              (response) =>
                response.questionId === question.id &&
                response.answerState === 'not answered',
            ),
          );

        setQuestions(questions);
        setQuestionResponses(initialQuestionResponses);
        setCurrentQuestionId(firstUnansweredQuestion?.id);

        onLoad?.(firstUnansweredQuestion, questions, initialQuestionResponses);
      });
  }, [article?.quizGeneratedAt?.getTime()]);

  useEffect(() => {
    if (currentQuestionId) {
      setQuestionStartTime(new Date());
    }
  }, [currentQuestionId]);

  const handleChooseAnswer = (chosenOptionIndex: number) => {
    if (currentQuestionResponse?.answerState === 'not answered') {
      setQuestionResponses((prevState) =>
        prevState.map((answer) =>
          answer.questionId === currentQuestion.id
            ? {
                ...currentQuestionResponse,
                chosenOptionIndex: chosenOptionIndex,
              }
            : answer,
        ),
      );
    }
  };

  const handleQuestionAnswered = async () => {
    if (
      currentQuestionResponse?.chosenOptionIndex === null ||
      currentQuestionResponse?.answerState !== 'not answered'
    ) {
      return;
    }

    const questionId = currentQuestion.id;

    const answerState =
      currentQuestion.correctOptionIndex ===
      currentQuestionResponse.chosenOptionIndex
        ? 'correct'
        : 'incorrect';

    const submissionTime = new Date();
    const responseTimeMs = questionStartTime
      ? submissionTime.getTime() - questionStartTime.getTime()
      : null;

    const oldQuestionResponses = questionResponses;
    const newQuestionResponses: QuestionResponse[] = questionResponses.map(
      (answer) =>
        answer.questionId === currentQuestion.id
          ? {
              ...currentQuestionResponse,
              answerState: answerState,
              isCompleted: true,
            }
          : answer,
    );

    const isLastQuestion = newQuestionResponses.every(
      (response) => response.isCompleted,
    );

    try {
      await trackEvent(AnalyticsEvent.QUIZ_QUESTION_ANSWERED, {
        source: 'QuizView',
        articleId: article?.id,
        article: article?.title,
        questionId: currentQuestion.id,
        question: currentQuestion.questionText,
        questionType: currentQuestion.questionType,
        answerState: answerState,
        startTime: questionStartTime?.toISOString(),
        submissionTime: submissionTime.toISOString(),
        responseTimeMs: responseTimeMs,
        userId: userId,
      });

      // Update the question responses with the new response
      setQuestionResponses(newQuestionResponses);

      // Don't bother showing the loot box and loader if user answered more than 1 question wrong
      setShowLootBox(checkIfHasChanceAtLootBox(newQuestionResponses));

      // Indicate that loot box is loading
      setLootBox(undefined);

      // Flush activity data before recording the question response
      await flushActivityData();

      const questionResponseApi = new QuestionResponseApi();
      const { userAchievements, lootBox: newLootBox } =
        await questionResponseApi.recordQuestionResponse(
          currentQuestion,
          currentQuestionResponse,
        );

      // If this is the last question, fetch rewards after backend has processed everything
      if (isLastQuestion) {
        const readingDataApi = new ReadingDataApi();
        await readingDataApi.fetchArticleStats(article.id);

        const rewards = await getRewardsByEventKey(
          `quiz-submitted-${article.id}`,
        );
        setArticleRewards(rewards);
        updateBalances();
      }

      // Grant loot box if awarded
      if (newLootBox) {
        setLootBox(newLootBox);
      } else {
        // If all questions have been answered, no loot box granted, won't ever be granted then
        if (isLastQuestion) {
          setLootBox(null);
        }
      }

      onQuestionAnswered(
        currentQuestion,
        currentQuestionResponse,
        questions,
        questionResponses,
        userAchievements,
        newLootBox,
      );
    } catch (error) {
      console.error('Error submitting quiz answer:', error);
      toast.error('Unable to submit your answer. Please try again.');

      // Reset the question response state
      setQuestionResponses(oldQuestionResponses);
      setShowLootBox(checkIfHasChanceAtLootBox(oldQuestionResponses));
      // No longer wait for the loot box to be granted
      setLootBox(null);
      // Revert current question to this failed one
      setCurrentQuestionId(questionId);

      // Track the error
      trackEvent(AnalyticsEvent.QUIZ_QUESTION_ANSWERED_FAILED, {
        source: 'QuizView',
        articleId: article?.id,
        article: article?.title,
        questionId: currentQuestion.id,
        question: currentQuestion.questionText,
        questionType: currentQuestion.questionType,
        error: error.message,
      });
    }
  };

  /**
   * Triggered when the user answers a question and clicks on the 'Next Question' button
   * This function records the answer and updates state with the next question
   * @param currentQuestion
   * @param questions
   * @param setCurrentQuestion
   * @param setCurrentQuestionResponse
   */
  const handleNextQuestion = () => {
    setQuestionResponses((prevState) =>
      prevState.map((response) =>
        response.questionId === currentQuestionId
          ? { ...response, isCompleted: true }
          : response,
      ),
    );

    console.log('Current Question Response:', currentQuestionResponse);

    if (
      currentQuestion.questionType === QuestionType.GUIDING &&
      currentQuestion.order === 0
    ) {
      trackEvent(AnalyticsEvent.STORY_ANSWERED_FIRST_QUESTION, {
        articleId: article?.id,
        article: article?.title,
        questionId: currentQuestion.id,
        question: currentQuestion.questionText,
        answerState: currentQuestionResponse?.answerState,
      });
    }

    const nextQuestion = getNextQuestion(currentQuestion, questions);

    if (nextQuestion) {
      if (currentQuestion.questionType === QuestionType.GUIDING) {
        if (nextQuestion.questionType === QuestionType.ASSESSMENT) {
          trackEvent(AnalyticsEvent.STORY_ANSWERED_ALL_GUIDING_QUESTIONS, {
            articleId: article?.id,
            article: article?.title,
            questionId: currentQuestion.id,
            question: currentQuestion.questionText,
            totalGuidingAnswers: guidingQuestionAnswers.length,
            correctGuidingAnswers: guidingQuestionAnswers.filter(
              (answer) => answer.answerState === 'correct',
            ).length,
          });
          onQuizSectionComplete?.(currentQuestion.questionType);
        }
      }

      setCurrentQuestionId(nextQuestion.id);
    } else {
      setCurrentQuestionId(null);
      onQuizSectionComplete?.(currentQuestion.questionType);
    }

    onNextQuestion?.(currentQuestion, nextQuestion);
  };

  const handleQuizCompleted = async () => {
    trackEvent(AnalyticsEvent.STORY_ANSWERED_ALL_QUESTIONS, {
      articleId: article?.id,
      article: article?.title,
      questionId: currentQuestion.id,
      question: currentQuestion.questionText,
      totalAnswers: questionResponses.length,
      correctAnswers: questionResponses.filter(
        (answer) => answer.answerState === 'correct',
      ).length,
    });

    onQuizComplete(questions, questionResponses);
  };

  const handleShowResults = async () => {
    await handleNextQuestion();
    await handleQuizCompleted();
  };

  const getNextQuestion = (
    currentQuestion: Question,
    questions: Question[],
  ): Question | null => {
    let nextQuestion = questions.find(
      (question) =>
        question.questionType === currentQuestion.questionType &&
        question.order === currentQuestion.order + 1,
    );
    if (
      !nextQuestion &&
      currentQuestion.questionType === QuestionType.GUIDING
    ) {
      nextQuestion = questions.find(
        (question) =>
          question.questionType === QuestionType.ASSESSMENT &&
          question.order === 0,
      );
    }
    return nextQuestion;
  };

  const getAssesmentStatusText = (): string => {
    if (assessmentState === AssessmentState.GuidingQuestions) {
      return `Guiding Questions ${(currentQuestion?.order ?? 0) + 1}/${guidingQuestionAnswers.length}`;
    }

    if (assessmentState === AssessmentState.AssessmentQuestions) {
      return `Quiz Questions ${(currentQuestion?.order ?? 0) + 1}/${assessmentQuestionAnswers.length}`;
    }

    if (assessmentState === AssessmentState.Complete) {
      return `Story Assessment Results`;
    }

    return `Guiding Questions 0/${guidingQuestionAnswers.length}`;
  };

  const checkIfHasChanceAtLootBox = (questionResponses: QuestionResponse[]) => {
    return (
      questionResponses.filter(
        (response) =>
          questionsById[response.questionId].questionType ===
            QuestionType.ASSESSMENT && response.answerState === 'incorrect',
      ).length <= 2
    );
  };

  if (!isLoaded) {
    // Height is set to the window size minus the height of the header navbar
    return (
      <div
        className="flex-1 flex justify-center items-center"
        style={{ height: 'calc(100dvh - 60px)' }}
      >
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div
      data-testid="quiz-view-section"
      className={`flex flex-col
        max-sm:mx-4 max-sm:rounded-xl max-sm:bg-gray-100
      `}
    >
      <div
        className="
          items-stretch flex flex-col md:p-3 xl:p-4 max-md:max-w-full
          max-sm:rounded-t-md max-sm:gap-1 max-sm:justify-between max-sm:p-4
          sm:bg-gray-50
        "
      >
        <div
          className="text-black text-xl font-medium
              max-sm:text-lg max-sm:font-bold
              max-md:max-w-full
          "
          data-testid="assessment-status-text"
        >
          {getAssesmentStatusText()}
        </div>
        {assessmentState === AssessmentState.GuidingQuestions && (
          <p
            className="self-stretch text-sm text-neutral-800 text-opacity-70
            sm:hidden
          "
          >
            Answer the guiding question to unlock and read the next part of the
            story.
          </p>
        )}
      </div>
      <div
        className="items-stretch flex gap-1 justify-center mx-4 overflow-hidden
          max-sm:justify-start max-sm:rounded-lg
          max-md:max-w-full max-md:flex-wrap"
      >
        {assessmentState === AssessmentState.NotStarted ||
        assessmentState === AssessmentState.GuidingQuestions ? (
          <QuizProgressBar
            questionAnswers={guidingQuestionAnswers}
            currentQuestionAnswer={currentQuestionResponse!}
          />
        ) : assessmentState === AssessmentState.AssessmentQuestions ? (
          <QuizProgressBar
            questionAnswers={assessmentQuestionAnswers}
            currentQuestionAnswer={currentQuestionResponse!}
          />
        ) : null}
      </div>

      {(assessmentState === AssessmentState.GuidingQuestions ||
        assessmentState === AssessmentState.AssessmentQuestions) && (
        <>
          <div className="items-end self-stretch flex w-full flex-col p-4 max-md:max-w-full">
            <div
              className="self-stretch text-neutral-800 text-base leading-6 max-md:max-w-full"
              data-testid="question-text"
            >
              <div className="flex gap-2">
                <div className="flex-1">{currentQuestion.questionText}</div>
                <div className="flex flex-col justify-start">
                  <button
                    onClick={() => {
                      setPlaybackState(
                        playbackState === AudioPlayerState.Play
                          ? AudioPlayerState.Stop
                          : AudioPlayerState.Play,
                      );
                      if (playbackState !== AudioPlayerState.Play) {
                        setIsPlaybackSectionLoaded(false);
                      }
                    }}
                    className="mr-4 p-1 hover:bg-gray-100 rounded"
                  >
                    <FontAwesomeIcon
                      icon={
                        !isPlaybackSectionLoaded &&
                        playbackState === AudioPlayerState.Play
                          ? faSpinner
                          : playbackState === AudioPlayerState.Play
                            ? faStop
                            : faVolumeHigh
                      }
                      className={`h-4 w-4 text-gray-600 ${!isPlaybackSectionLoaded && playbackState === AudioPlayerState.Play ? 'fa-spin' : ''}`}
                    />
                  </button>
                </div>
              </div>
            </div>
            <TextToSpeech
              key={`quiz-question-${currentQuestion.id}`}
              descriptorUrl={`${window.env.REACT_APP_API_URL}/tts/quiz/question/${currentQuestion.id}`}
              manager={audioManager}
              state={playbackState}
              preload={false}
              analyticsContext={{
                type: 'quiz_question',
                questionId: currentQuestion.id,
                articleId: article.id,
              }}
              onPlay={() => {
                setPlaybackState(AudioPlayerState.Play);
                setIsPlaybackSectionLoaded(true);
              }}
              onPause={() => setPlaybackState(AudioPlayerState.Stop)}
              onEnd={() => setPlaybackState(AudioPlayerState.Stop)}
            />

            {currentQuestion?.questionOptions.map((option, index) => (
              <QuizQuestionOption
                key={`quiz-question-option-${currentQuestion.id}-${index}`}
                index={index}
                option={option}
                currentQuestion={currentQuestion}
                currentQuestionResponse={currentQuestionResponse}
                handleOptionClick={handleChooseAnswer}
              />
            ))}
          </div>

          {currentQuestionResponse?.answerState === 'not answered' ? (
            currentQuestion.questionType === QuestionType.GUIDING ? (
              <QuizActionButton
                testId="answer-and-continue-reading-button"
                label="Answer & Continue Reading"
                onClick={handleQuestionAnswered}
              />
            ) : !isUserAnonymous ? (
              <QuizActionButton
                testId="submit-answer-button"
                label="Submit"
                onClick={handleQuestionAnswered}
              />
            ) : (
              <SignUpNotice />
            )
          ) : (
            <>
              {currentQuestion.questionType === QuestionType.GUIDING && (
                <>
                  {getNextQuestion(currentQuestion, article.questions)
                    ?.questionType === QuestionType.GUIDING ? (
                    <QuizActionButton
                      testId="continue-reading-button"
                      label="Continue Reading"
                      onClick={handleNextQuestion}
                    />
                  ) : (
                    <QuizActionButton
                      testId="keep-reading-button"
                      label="Keep Reading"
                      onClick={handleNextQuestion}
                    />
                  )}
                </>
              )}
              {currentQuestion.questionType === QuestionType.ASSESSMENT && (
                <>
                  {getNextQuestion(currentQuestion, article.questions) ? (
                    <QuizActionButton
                      testId="next-question-button"
                      label="Next Question"
                      onClick={() => handleNextQuestion()}
                    />
                  ) : (
                    <QuizActionButton
                      testId="show-results-button"
                      label="Show Results"
                      onClick={handleShowResults}
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      {assessmentState === AssessmentState.Complete && (
        <>
          <div className="flex gap-4 h-[250px] overflow-hidden items-center justify-center">
            {/* The difference in iframe height and container height is intentional. Survey height is too large and can't be changed. Reducing iframe height introduces scroll. So we center the iframe in the container to get proper UX. */}
            <iframe
              src={`${getSurvicateSurveyUrl(SURVICATE_INSTANCE.config.storyFeedbackSurveyId)}/?user_id=${user?.id}&email=${user?.email}&article_id=${article.id}&article_title=${article.title}`}
              title="Satisfaction Survey"
              width="100%"
              className="border-none h-[290px]"
            />
          </div>
          <QuizResultSummary
            article={article}
            guidingQuestionAnswers={guidingQuestionAnswers}
            assessmentQuestionAnswers={assessmentQuestionAnswers}
            rewards={articleRewards}
            lootBox={lootBox}
            showLootBox={showLootBox}
          />
        </>
      )}
    </div>
  );
};

const SignUpNotice = () => {
  const navigate = useNavigate();

  return (
    <section className="flex flex-col self-stretch p-2 m-4 text-center rounded-lg border border-solid bg-yellow-300 bg-opacity-10 border-yellow-500 border-opacity-20 max-w-[536px]">
      <h2 className="text-2xl my-4 font-semibold text-center text-black">
        Experience the Full Adventure!
      </h2>
      <p className="mt-1.5 text-sm text-neutral-800 text-opacity-70 max-md:max-w-full">
        You've experienced a demo of our Story Builder. Create an account to
        unlock the full experience, including unlimited stories, earning rewards
        by completing quizzes, social reading circles, and more!
      </p>
      <p>
        <button
          className="mt-3 mb-1.5 text-white text-center text-base whitespace-nowrap justify-center items-center self-center border border-[color:var(--primary,#0D6EFD)] bg-blue-600 max-w-full px-4 py-3 rounded-lg border-solid max-md:px-5 hover:bg-blue-400"
          onClick={() => {
            if (window.parent) {
              window.parent.postMessage('signup', '*');
            } else {
              navigate('/signup', { redirectUrl: window.location.pathname });
            }
          }}
        >
          Create an Account to Continue
        </button>
      </p>
    </section>
  );
};

export default QuizView;
